import { LucideIcon, Zap, Video, Phone, FileText, Globe, Compass } from "lucide-react";

export interface ItinerarioStep {
  number: number;
  title: string;
  description: string;
  estimatedTime: string;
  articleIds: string[];
  capsulaId?: string;
}

export interface Itinerario {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  difficulty: "Fácil" | "Media" | "Avanzada";
  totalTime: string;
  benefits: string[];
  targetAudience: string[];
  steps: ItinerarioStep[];
}

export const itinerarios: Itinerario[] = [
  {
    id: "automatiza-negocio",
    title: "Automatiza tu negocio en 4 semanas",
    description: "Identifica qué tareas te roban tiempo y automatízalas paso a paso. Desde emails hasta facturas, sin tocar código.",
    icon: Zap,
    difficulty: "Media",
    totalTime: "4 semanas",
    benefits: [
      "Recupera hasta 15 horas semanales",
      "Automatiza emails, facturas y consultas",
      "Sin conocimientos técnicos necesarios",
      "Resultados medibles desde la semana 1"
    ],
    targetAudience: ["Gestorías", "Asesorías", "Consultoras", "Despachos", "Ecommerce", "Agencias"],
    steps: [
      {
        number: 1,
        title: "Diagnostica qué tareas te roban tiempo",
        description: "Identifica los procesos repetitivos de tu negocio y evalúa cuáles son candidatos para automatizar.",
        estimatedTime: "30 minutos",
        articleIds: ["31"],
        capsulaId: "automatizacion-emails"
      },
      {
        number: 2,
        title: "Automatiza emails y facturas",
        description: "Configura herramientas de IA para que gestionen emails rutinarios y procesen facturas automáticamente.",
        estimatedTime: "1 hora",
        articleIds: ["16", "35", "67"],
        capsulaId: "automatizacion-emails"
      },
      {
        number: 3,
        title: "Conecta WhatsApp con tu flujo de ventas",
        description: "Integra un chatbot inteligente en WhatsApp para atender consultas y cerrar ventas mientras duermes.",
        estimatedTime: "45 minutos",
        articleIds: ["33", "32"],
      },
      {
        number: 4,
        title: "Mide resultados y escala",
        description: "Analiza el tiempo ahorrado, ajusta los flujos y extiende la automatización a más áreas de tu negocio.",
        estimatedTime: "30 minutos",
        articleIds: ["47", "15", "52"],
      }
    ]
  },
  {
    id: "contenido-profesional",
    title: "Crea contenido profesional sin equipo",
    description: "Vídeos, presentaciones y diseños de nivel profesional. Solo necesitas tu ordenador y estas herramientas.",
    icon: Video,
    difficulty: "Fácil",
    totalTime: "2 semanas",
    benefits: [
      "Crea vídeos sin cámara ni editor",
      "Presentaciones listas en 10 minutos",
      "Diseños para redes sociales al instante",
      "Calidad profesional, coste cero"
    ],
    targetAudience: ["Agencias", "Ecommerce", "Academias", "Franquicias", "Tiendas"],
    steps: [
      {
        number: 1,
        title: "Crea tu primer vídeo con IA",
        description: "Genera vídeos profesionales desde texto o guiones simples, sin necesidad de cámara ni equipo de grabación.",
        estimatedTime: "15 minutos",
        articleIds: ["17", "11"],
        capsulaId: "creacion-videos"
      },
      {
        number: 2,
        title: "Diseña presentaciones en minutos",
        description: "Convierte una idea en una presentación pulida usando PowerPoint con IA o Google Vids.",
        estimatedTime: "10 minutos",
        articleIds: ["6", "19"],
        capsulaId: "creacion-presentaciones"
      },
      {
        number: 3,
        title: "Genera diseños visuales para redes",
        description: "Crea imágenes, carruseles y diseños de marca usando herramientas de IA gratuitas.",
        estimatedTime: "20 minutos",
        articleIds: ["14", "26", "28", "63"],
        capsulaId: "redes-sociales"
      },
      {
        number: 4,
        title: "Planifica y programa tu contenido",
        description: "Organiza tu calendario de publicación y usa IA para mantener una presencia constante en redes.",
        estimatedTime: "20 minutos",
        articleIds: ["13", "21"],
        capsulaId: "redes-sociales"
      }
    ]
  },
  {
    id: "atencion-cliente-247",
    title: "Atiende clientes 24/7 sin contratar",
    description: "Un asistente virtual que responde llamadas, WhatsApp y consultas mientras tú te centras en lo importante.",
    icon: Phone,
    difficulty: "Media",
    totalTime: "1 semana",
    benefits: [
      "Atención al cliente 24 horas, 7 días",
      "Sin contratar personal adicional",
      "Respuestas coherentes y profesionales",
    ],
    targetAudience: ["Restaurantes", "Clínicas", "Hoteles", "Gimnasios", "Inmobiliarias", "Tiendas"],
    steps: [
      {
        number: 1,
        title: "Configura un agente de voz",
        description: "Crea un asistente que atienda llamadas telefónicas, tome pedidos y responda preguntas frecuentes.",
        estimatedTime: "45 minutos",
        articleIds: ["4"],
        capsulaId: "agentes-voz"
      },
      {
        number: 2,
        title: "Activa un chatbot en WhatsApp",
        description: "Conecta un chatbot inteligente a tu WhatsApp Business para gestionar consultas automáticamente.",
        estimatedTime: "30 minutos",
        articleIds: ["33", "32"],
      },
      {
        number: 3,
        title: "Entrena a tu asistente con tu información",
        description: "Alimenta al asistente con tus documentos, precios y políticas para que responda con precisión.",
        estimatedTime: "30 minutos",
        articleIds: ["38", "18"],
        capsulaId: "analisis-documentos"
      }
    ]
  },
  {
    id: "digitaliza-administracion",
    title: "Digitaliza tu administración",
    description: "Facturas, documentos, reuniones y emails gestionados por IA. Libera a tu equipo del papeleo.",
    icon: FileText,
    difficulty: "Fácil",
    totalTime: "3 semanas",
    benefits: [
      "Procesa facturas automáticamente",
      "Encuentra información al instante",
      "Documenta procesos sin esfuerzo",
      "Reuniones con actas automáticas"
    ],
    targetAudience: ["Gestorías", "Asesorías", "Despachos", "Consultoras", "Franquicias"],
    steps: [
      {
        number: 1,
        title: "Automatiza el procesado de facturas",
        description: "Configura una herramienta que lea, clasifique y registre facturas sin intervención manual.",
        estimatedTime: "30 minutos",
        articleIds: ["16"],
      },
      {
        number: 2,
        title: "Crea tu base de conocimiento",
        description: "Sube tus documentos a un asistente IA que responda consultas internas al instante.",
        estimatedTime: "20 minutos",
        articleIds: ["38", "18", "48", "53", "54", "55"],
        capsulaId: "analisis-documentos"
      },
      {
        number: 3,
        title: "Automatiza actas y notas de reunión",
        description: "Usa IA para transcribir reuniones y generar planes de acción automáticos.",
        estimatedTime: "15 minutos",
        articleIds: ["35", "34"],
      },
      {
        number: 4,
        title: "Documenta tus procesos internos",
        description: "Genera documentación paso a paso de cualquier proceso sin escribir una sola línea.",
        estimatedTime: "15 minutos",
        articleIds: ["39"],
      }
    ]
  },
  {
    id: "presencia-online",
    title: "Lanza tu presencia online en 1 semana",
    description: "De la idea a una web profesional con contenido visual. Sin diseñador, sin programador, sin esperas.",
    icon: Globe,
    difficulty: "Fácil",
    totalTime: "1 semana",
    benefits: [
      "Web profesional en horas, no semanas",
      "Contenido visual de calidad incluido",
      "Sin coste de diseñador ni programador"
    ],
    targetAudience: ["Tiendas", "Restaurantes", "Gimnasios", "Academias", "Clínicas", "Inmobiliarias"],
    steps: [
      {
        number: 1,
        title: "Genera tu web desde una descripción",
        description: "Usa herramientas de IA para crear una web completa a partir de una simple descripción de tu negocio.",
        estimatedTime: "2 horas",
        articleIds: ["27", "22", "62", "66"],
      },
      {
        number: 2,
        title: "Crea el contenido visual",
        description: "Genera fotos, vídeos y gráficos profesionales para tu web sin necesidad de sesión fotográfica.",
        estimatedTime: "1 hora",
        articleIds: ["11", "28", "26"],
        capsulaId: "creacion-videos"
      },
      {
        number: 3,
        title: "Publica y promociona",
        description: "Lanza tu web, configura redes sociales y empieza a generar contenido para atraer clientes.",
        estimatedTime: "1 hora",
        articleIds: ["13", "14", "21"],
        capsulaId: "redes-sociales"
      }
    ]
  },
  {
    id: "prepara-empresa-ia",
    title: "Prepara tu empresa para la IA",
    description: "Antes de invertir en herramientas, asegúrate de que tu negocio está listo. Diagnostica, ordena y planifica en 3 pasos.",
    icon: Compass,
    difficulty: "Fácil",
    totalTime: "1 semana",
    benefits: [
      "Evita el 70% de fracasos en proyectos de IA",
      "Identifica tu nivel de madurez digital",
      "Plan de acción claro antes de invertir",
      "Sin coste: solo tu tiempo y estas guías"
    ],
    targetAudience: ["Gestorías", "Asesorías", "Consultoras", "Ecommerce", "Agencias", "Clínicas", "Tiendas"],
    steps: [
      {
        number: 1,
        title: "Diagnostica tu madurez digital",
        description: "Evalúa en qué punto está tu negocio: procesos, herramientas y equipo. Identifica qué necesitas antes de implementar IA.",
        estimatedTime: "15 minutos",
        articleIds: ["31"],
        capsulaId: "estrategia-ia"
      },
      {
        number: 2,
        title: "Conoce qué puede hacer la IA por tu negocio",
        description: "Descubre casos reales de empresas como la tuya que ya usan IA y entiende qué es viable para tu sector.",
        estimatedTime: "30 minutos",
        articleIds: ["41", "47"],
        capsulaId: "estrategia-ia"
      },
      {
        number: 3,
        title: "Elige tu primera automatización",
        description: "Selecciona el proceso más rentable para automatizar primero y configúralo con herramientas gratuitas.",
        estimatedTime: "30 minutos",
        articleIds: ["16", "35"],
        capsulaId: "automatizacion-emails"
      }
    ]
  }
];
